// Per-element seat-state painter.
//
// Previously this module re-queried the entire DOM (`document.querySelectorAll`)
// on every state change. The new implementation maintains a registry keyed by
// `seatGuid`, populated by `GroupRenderer` while building the SVG, and only
// updates classes on circles whose state actually changed.
//
// `applySeatStates` is the public entry point. `seatRegistry` and
// `tableRegistry` are written to by `GroupRenderer` (cleared via
// `resetSeatRegistry` on each rebuild).

import type { Guest, SeatToGuestMap } from '../types';
import { MIN_GUESTS } from '../config';

/** Map of seatGuid -> seat <circle> element. Owned by GroupRenderer. */
export const seatRegistry = new Map<string, SVGCircleElement>();

/** Per-table render entry. */
interface TableEntry {
  tableGroupEl: SVGGElement;
  segmentEl?: SVGCircleElement;
  seats: Set<string>;
}
/** Map of tableGuid -> render entry. Owned by GroupRenderer. */
export const tableRegistry = new Map<string, TableEntry>();

/** Last-known state per seatGuid; lets us skip class mutations when nothing changed. */
const lastSeatState = new Map<string, string>();

const SEAT_CLASSES = [
  'seat-assigned',
  'seat-occupied',
  'seat-available',
  'seat-selected',
  'seat-confirmed',
  'seat-conflict',
  'seat-unavailable',
] as const;

type SeatState = 'seat-selected' | 'seat-confirmed' | 'seat-conflict' | 'seat-occupied' | 'seat-available';

/** Reset all caches; call from GroupRenderer before a full SVG rebuild. */
export function resetSeatRegistry(): void {
  seatRegistry.clear();
  tableRegistry.clear();
  lastSeatState.clear();
}

/** Diff-based seat painter. Only mutates DOM for seats whose state changed. */
export function applySeatStates(seatToGuestMap?: SeatToGuestMap, guestCodes?: Guest[]): void {
  if (!seatToGuestMap) return;
  const sessionId = window.sessionStorage.getItem('sessionId');
  const enteredGuestCodes = guestCodes
    ? guestCodes.filter(g => g.guestCode && g.guestCode.trim() !== '').length
    : 0;
  const minGuestCodes = Math.max(enteredGuestCodes, MIN_GUESTS);

  // Per-table available-seat tally, recomputed each call.
  const tableAvailable = new Map<string, number>();
  for (const tg of tableRegistry.keys()) tableAvailable.set(tg, 0);

  for (const [seatGuid, circle] of seatRegistry) {
    const assigned = seatToGuestMap[seatGuid];
    let state: SeatState;
    if (assigned && assigned.guestGuid != null) {
      const myGuest = guestCodes?.find(g => g.guestGuid === assigned.guestGuid);
      if (myGuest) {
        if (assigned.confirmed === true) {
          state = 'seat-confirmed';
        } else {
          state = sessionId === assigned.sessionId ? 'seat-selected' : 'seat-conflict';
        }
      } else {
        state = 'seat-occupied';
      }
    } else {
      state = 'seat-available';
    }

    if (lastSeatState.get(seatGuid) !== state) {
      for (const cls of SEAT_CLASSES) circle.classList.remove(cls);
      circle.classList.add(state);
      lastSeatState.set(seatGuid, state);
      // a11y: keep aria-pressed in sync for assistive tech.
      if (circle.hasAttribute('aria-pressed')) {
        circle.setAttribute('aria-pressed', state === 'seat-selected' ? 'true' : 'false');
      }
    }

    if (state === 'seat-available') {
      const tableGuid = circle.getAttribute('data-table-guid');
      if (tableGuid) tableAvailable.set(tableGuid, (tableAvailable.get(tableGuid) || 0) + 1);
    }
  }

  const guestListGuids = (guestCodes || [])
    .filter(g => g.guestCode && g.guestCode.trim() !== '')
    .map(g => g.guestGuid);

  for (const [tableGuid, entry] of tableRegistry) {
    const assignedHere: string[] = [];
    for (const seatGuid of entry.seats) {
      const a = seatToGuestMap[seatGuid];
      if (a?.guestGuid) assignedHere.push(a.guestGuid);
    }
    const allMyGuestsAtTable = guestListGuids.length > 0
      && guestListGuids.every(g => assignedHere.includes(g));
    const available = tableAvailable.get(tableGuid) || 0;
    const unavailable = available < minGuestCodes && !allMyGuestsAtTable;

    entry.tableGroupEl.classList.toggle('table-unavailable', unavailable);
    entry.segmentEl?.classList.toggle('table-occupied', unavailable);
  }
}

/**
 * Backwards-compatible alias used by older callers. Behaves exactly like
 * applySeatStates; with an empty registry it is a fast no-op.
 */
export function updateSeatStates(seatToGuestMap?: SeatToGuestMap, guestCodes?: Guest[]): void {
  applySeatStates(seatToGuestMap, guestCodes);
}
